// Protocol types and staircase builders. Mirrors backend/vtlite/protocols.py and scoring.py;
// GET /api/protocols/default is the runtime source of truth for the defaults.

import { DoubleStaircase, type DoubleParams, type InterleavedParams } from "./staircase";
import { letterArcminToLogmar } from "./units";

export type Eye = "OD" | "OS" | "OU";
export type Orientation = "right" | "left" | "up" | "down";
export const ORIENTATIONS: readonly Orientation[] = ["right", "left", "up", "down"];
export type ResponseKey = Orientation | "none";

export interface Timing {
  stimulus_ms: number;
  isi_ms: number;
  response_timeout_ms: number;
}

/** Linear fraction of each primary's maximum (rel), red, green, blue. */
export type RGB = [number, number, number];

/** visualTools' chromatic edge jitter (pipelineChromJitter); mirrors protocols.EdgeJitter. */
export interface EdgeJitter {
  enabled: boolean;
  /** "stroke": widths in E strokes (scale with the letter); "px": device pixels. */
  unit: "stroke" | "px";
  inner_width: number;
  outer_width: number;
  blob_size: number;
  density: number;
  update_frames: number;
  intensity: number;
  link_to_contrast: boolean;
  axis: string;
  color1_rgb_rel: RGB;
  color2_rgb_rel: RGB;
  /** "cone_isolating": the server computes +L-M / -L+M for the session's display. */
  colour_source: "preset" | "cone_isolating";
  cone_mode: "cone_contrast" | "isoluminant";
  cone_center: "mid_grey" | "background";
  cone_observer: ConeObserver;
  cone_result: ConeResult | null;
}

export interface ConeObserver {
  fundamentals: "neitz" | "stockman_sharpe_2";
  l_peak: number;
  m_peak: number;
  s_peak: number;
  age: number;
  macular_density_weight: number;
  lens_model: "stockman" | "stiles_raw" | "pokorny" | "vannorren";
}

export interface ConeContrast { L: number; M: number; S: number; luminance: number | null }
export interface ConeResult {
  mode: string; primaries: string; center_rgb_rel: RGB;
  plus_l_minus_m_rgb_rel: RGB; minus_l_plus_m_rgb_rel: RGB;
  cone_contrast_plus: ConeContrast; cone_contrast_minus: ConeContrast; l_minus_m_contrast: number;
}

export const DEFAULT_CONE_OBSERVER: ConeObserver = {
  fundamentals: "neitz", l_peak: 559, m_peak: 530, s_peak: 420, age: 32, macular_density_weight: 1, lens_model: "stockman",
};
export const CONE_AXIS_LABEL = "Cone-isolating L vs M (for the display)";

export const JITTER_AXES: Record<string, [RGB, RGB]> = {
  "+L-M / -L+M": [[1, 0, 0], [0, 1, 0]],
  "+S-(L+M) / -(S-LM)": [[0, 0, 1], [1, 1, 0]],
  "L-cone / -L": [[1, 0, 0], [0, 0.5, 0.5]],
  "M-cone / -M": [[0, 1, 0], [0.5, 0, 0.5]],
  "S-cone / -S": [[0, 0, 1], [0.5, 0.5, 0]],
  "Achromatic +/-": [[1, 1, 1], [0, 0, 0]],
};

export const DEFAULT_EDGE_JITTER: EdgeJitter = {
  enabled: false, unit: "stroke", inner_width: 0.2, outer_width: 0.2, blob_size: 0.1,
  density: 1.0, update_frames: 1, intensity: 1.0, link_to_contrast: true, axis: "+L-M / -L+M",
  color1_rgb_rel: [1, 0, 0], color2_rgb_rel: [0, 1, 0],
  colour_source: "preset", cone_mode: "cone_contrast", cone_center: "mid_grey",
  cone_observer: { ...DEFAULT_CONE_OBSERVER }, cone_result: null,
};

/** Defaults when switching units: visualTools' pixel values, or their stroke equivalents. */
export const JITTER_UNIT_DEFAULTS: Record<EdgeJitter["unit"], Pick<EdgeJitter, "inner_width" | "outer_width" | "blob_size">> = {
  stroke: { inner_width: 0.2, outer_width: 0.2, blob_size: 0.1 },
  px: { inner_width: 5, outer_width: 5, blob_size: 1 },
};

export interface Appearance {
  /** null: grey at the protocol's background_rel. */
  background_rgb_rel: RGB | null;
  letter_rgb_rel: RGB;
  /** "luminance": the contrast letter is a darker shade of the background (log CS);
   *  "toward_letter": it moves from the background toward the letter colour (log modulation). */
  contrast_mode: "luminance" | "toward_letter";
}

export const DEFAULT_APPEARANCE: Appearance = { background_rgb_rel: null, letter_rgb_rel: [0, 0, 0], contrast_mode: "luminance" };

export type TestKind = "acuity" | "contrast";

export interface AcuityConfig {
  method: "quest" | "levitt" | "weighted" | "simple";
  min_logmar: number;
  max_logmar: number;
  quest_trials: number;
  quest_prior_mean: number;
  quest_prior_sd: number;
  quest_beta: number;
  quest_gamma: number;
  quest_delta: number;
  quest_grain: number;
  quest_initial_easy: number;
  quest_descent_step: number;
  start_low: number;
  start_high: number;
  step: number;
  step_factor: number;
  levitt_n_down: number;
  weighted_target: number;
  max_reversals: number;
  reversals_to_average: number;
  catch_rate: number;
  catch_min_pct: number;
  catch_action: "flag" | "invalid";
  timing: Timing;
  edge_jitter: EdgeJitter;
}

export type StaircaseType = "weighted" | "quest" | "levitt" | "simple";

export interface ContrastConfig {
  /** staircases: interleaved staircases, averaged (default); pelli_robson: the chart's
   *  descending levels; quest: the older QUEST pair (kept for existing programs). */
  method: "staircases" | "pelli_robson" | "quest";
  size_rule: "pelli_robson" | "acuity_relative";
  pelli_robson_letter_deg: number;
  acuity_offset_logmar: number;
  gate_trials: number;
  gate_step_logmar: number;
  gate_max_attempts: number;
  pr_step_log_cs: number;
  pr_max_log_cs: number;
  pr_group_size: number;
  pr_pass_correct: number;
  staircase_type: StaircaseType;
  n_staircases: number;
  trials_per_staircase: number;
  start_log_cs_easy: number;
  start_log_cs_hard: number;
  step_log_cs: number;
  weighted_target: number;
  levitt_n_down: number;
  discard_reversals: number;
  staircase_max_log_cs: number;
  /** How the staircases share the trials, and when each stops (see staircase.ts). */
  stagger: "none" | "fixed" | "random";
  stagger_trials: number;
  pick: "random" | "balanced";
  stop_rule: "trials" | "settled";
  stop_reversals: number;
  stop_sd_log: number;
  catch_rate: number;
  catch_log_cs: number;
  catch_min_pct: number;
  catch_action: "flag" | "invalid";
  /** Set when a test starts: the seed of its trial order (the server replays it). */
  planner_seed: number | null;
  quest_trials: number;
  quest_min_log_weber: number;
  quest_prior_mean: number;
  quest_prior_sd: number;
  quest_beta: number;
  quest_gamma: number;
  quest_delta: number;
  quest_grain: number;
  timing: Timing;
  edge_jitter: EdgeJitter;
}

export interface HomeChecks {
  brightness: "every_session" | "off";
  monitor_id: "off" | "optional" | "required";
  allow_hdr_pq: boolean;
}

export interface ChangeAlerts {
  baseline: "clinic_if_available" | "clinic_only" | "any";
  baseline_n: number;
  ask_repeat: boolean;
  tell_patient: boolean;
  patient_message: string;
  contact: string;
}

/** One test as an Experiment defines it: which test, its look and its method. */
export interface ExperimentConfig {
  kind: TestKind;
  background_rel: number;
  appearance: Appearance;
  acuity: AcuityConfig | null;
  contrast: ContrastConfig | null;
}

/** One experiment in a program, presented `times` times (per eye); its settings as copied
 *  in when the program was saved (`experiment_version`). */
export interface ProgramStep {
  experiment_id: number | null;
  experiment_version: number | null;
  name: string;
  times: number;
  experiment: ExperimentConfig;
}

export type EyeOrder = "eye_by_eye" | "step_by_step";

export interface Protocol {
  name: string;
  version: number;
  eyes: Eye[];
  eye_order: EyeOrder;
  steps: ProgramStep[];
  viewing_distance_mm: number;
  target_white_cd_m2: number;
  show_each_result: boolean;
  home: HomeChecks;
  alerts: ChangeAlerts;
}

/** What a test of a session knows about its experiment (TestInfo.experiment). */
export interface TestExperiment {
  id: number | null; version: number | null; name: string; step: number; repeat: number; times: number;
  background_rel: number; appearance: Appearance;
}

/** The look of a test: its experiment's (sessions from before experiments: the program's first
 *  step of that kind). */
export function testLook(protocol: Protocol, test: { kind: TestKind; experiment?: TestExperiment | null }):
  { background_rel: number; appearance: Appearance } {
  if (test.experiment) return { background_rel: test.experiment.background_rel, appearance: test.experiment.appearance };
  const st = protocol.steps.find((x) => x.experiment.kind === test.kind) ?? protocol.steps[0];
  return st ? { background_rel: st.experiment.background_rel, appearance: st.experiment.appearance }
    : { background_rel: 1, appearance: DEFAULT_APPEARANCE };
}

export function defaultExperiment(kind: TestKind): ExperimentConfig {
  return {
    kind, background_rel: 1, appearance: { ...DEFAULT_APPEARANCE },
    acuity: kind === "acuity" ? structuredClone(DEFAULT_ACUITY) : null,
    contrast: kind === "contrast" ? structuredClone(DEFAULT_CONTRAST) : null,
  };
}

export const DEFAULT_TIMING: Timing = { stimulus_ms: 0, isi_ms: 500, response_timeout_ms: 0 };

export const DEFAULT_ACUITY: AcuityConfig = {
  method: "quest", min_logmar: -0.3, max_logmar: 1.0,
  quest_trials: 20, quest_prior_mean: 0.3, quest_prior_sd: 0.5, quest_beta: 15.0,
  quest_gamma: 0.25, quest_delta: 0.04, quest_grain: 0.01, quest_initial_easy: 1,
  quest_descent_step: 0.3,
  start_low: 0.0, start_high: 1.0, step: 0.2, step_factor: 0.5, levitt_n_down: 3,
  weighted_target: 0.75, max_reversals: 10, reversals_to_average: 6,
  catch_rate: 0.1, catch_min_pct: 80, catch_action: "flag",
  timing: { ...DEFAULT_TIMING }, edge_jitter: structuredClone(DEFAULT_EDGE_JITTER),
};

export const DEFAULT_STAIRCASES = {
  staircase_type: "weighted" as StaircaseType, n_staircases: 4, trials_per_staircase: 15,
  start_log_cs_easy: 0.9, start_log_cs_hard: 1.8, step_log_cs: 0.3, weighted_target: 0.75,
  levitt_n_down: 3, discard_reversals: 2, staircase_max_log_cs: 2.5,
  stagger: "none" as ContrastConfig["stagger"], stagger_trials: 3, pick: "random" as ContrastConfig["pick"],
  stop_rule: "trials" as ContrastConfig["stop_rule"], stop_reversals: 6, stop_sd_log: 0.08,
  catch_rate: 0, catch_log_cs: 0, catch_min_pct: 80, catch_action: "flag" as ContrastConfig["catch_action"],
  planner_seed: null as number | null,
};

export const DEFAULT_CONTRAST: ContrastConfig = {
  method: "staircases", size_rule: "pelli_robson", pelli_robson_letter_deg: 2.8,
  acuity_offset_logmar: 0.3, gate_trials: 4, gate_step_logmar: 0.1, gate_max_attempts: 5,
  pr_step_log_cs: 0.15, pr_max_log_cs: 2.25, pr_group_size: 5, pr_pass_correct: 4,
  ...DEFAULT_STAIRCASES,
  quest_trials: 30, quest_min_log_weber: -2.5, quest_prior_mean: -1.5, quest_prior_sd: 0.8,
  quest_beta: 15.0, quest_gamma: 0.25, quest_delta: 0.04, quest_grain: 0.01,
  timing: { ...DEFAULT_TIMING }, edge_jitter: structuredClone(DEFAULT_EDGE_JITTER),
};

/** Background colour of an experiment (older programs have only a grey level). */
export function backgroundRgb(p: { background_rel: number; appearance?: Appearance }): RGB {
  return p.appearance?.background_rgb_rel ?? [p.background_rel, p.background_rel, p.background_rel];
}

export function buildAcuityStaircase(cfg: AcuityConfig, rng: () => number): DoubleStaircase {
  return new DoubleStaircase(acuityParams(cfg), rng);
}

export function acuityParams(cfg: AcuityConfig): DoubleParams {
  const lo = cfg.min_logmar;
  const hi = cfg.max_logmar;
  if (cfg.method === "quest") {
    return {
      method: "quest", paramName: "logmar", startLow: hi, startHigh: hi, stepSize: 0,
      minValue: lo, maxValue: hi, maxTrials: cfg.quest_trials, priorMean: cfg.quest_prior_mean,
      priorSd: cfg.quest_prior_sd, beta: cfg.quest_beta, gamma: cfg.quest_gamma,
      delta: cfg.quest_delta, grain: cfg.quest_grain, nInitialEasy: cfg.quest_initial_easy,
      descentStep: cfg.quest_descent_step,
    };
  }
  const clamp = (v: number) => Math.min(Math.max(v, lo), hi);
  const common = {
    paramName: "logmar", startLow: clamp(cfg.start_low), startHigh: clamp(cfg.start_high),
    stepSize: cfg.step, minValue: lo, maxValue: hi, maxReversals: cfg.max_reversals,
    reversalsToAverage: cfg.reversals_to_average, stepFactor: cfg.step_factor,
  };
  if (cfg.method === "levitt") {
    return { ...common, method: "levitt", nDown: cfg.levitt_n_down, nUp: 1 };
  }
  const target = cfg.method === "simple" ? 0.5 : cfg.weighted_target;
  return {
    ...common, method: "weighted", nDown: 1, nUp: 1, stepDownSize: cfg.step,
    stepUpSize: (cfg.step * target) / (1.0 - target),
  };
}

export function contrastQuestParams(cfg: ContrastConfig): DoubleParams {
  return {
    method: "quest", paramName: "log_weber", startLow: 0, startHigh: 0, stepSize: 0,
    minValue: cfg.quest_min_log_weber, maxValue: 0, maxTrials: cfg.quest_trials,
    priorMean: cfg.quest_prior_mean, priorSd: cfg.quest_prior_sd, beta: cfg.quest_beta,
    gamma: cfg.quest_gamma, delta: cfg.quest_delta, grain: cfg.quest_grain, nInitialEasy: 1,
    descentStep: 0.3,
  };
}

/** Each staircase's starting log CS, easiest first, spread evenly (mirrors Python). */
export function staircaseStartsLogCs(cfg: ContrastConfig): number[] {
  const n = cfg.n_staircases;
  const a = cfg.start_log_cs_easy;
  const b = cfg.start_log_cs_hard;
  if (n === 1) return [(a + b) / 2];
  return Array.from({ length: n }, (_, i) => a + ((b - a) * i) / (n - 1));
}

/** The contrast staircases on log10 Weber (mirrors scoring.build_contrast_staircases). */
export function contrastStaircaseParams(cfg: ContrastConfig): InterleavedParams {
  const starts = staircaseStartsLogCs(cfg).map((v) => -v);
  const common = {
    paramName: "log_weber", starts, minValue: -cfg.staircase_max_log_cs, maxValue: 0,
    maxTrials: cfg.trials_per_staircase, discardReversals: cfg.discard_reversals,
    stagger: cfg.stagger, staggerTrials: cfg.stagger_trials, pick: cfg.pick,
    stopRule: cfg.stop_rule, stopReversals: cfg.stop_reversals, stopSd: cfg.stop_sd_log,
    catchRate: cfg.catch_rate, catchValue: -cfg.catch_log_cs,
  };
  const t = cfg.staircase_type;
  if (t === "quest") {
    return {
      ...common, method: "quest", priorMean: cfg.quest_prior_mean, priorSd: cfg.quest_prior_sd,
      beta: cfg.quest_beta, gamma: cfg.quest_gamma, delta: cfg.quest_delta, grain: cfg.quest_grain,
      nInitialEasy: 0, descentStep: cfg.step_log_cs,
    };
  }
  const step = cfg.step_log_cs;
  let nDown = 1;
  let up = step;
  if (t === "levitt") nDown = cfg.levitt_n_down;
  else if (t === "weighted") up = (step * cfg.weighted_target) / (1.0 - cfg.weighted_target);
  return {
    ...common, method: t, stepSize: step, stepFactor: 0.5, nDown, nUp: 1, stepDownSize: step,
    stepUpSize: up, reduceAtReversals: [1, 2],
  };
}

/** Older programs miss the staircase settings: fill them from the defaults. */
export function withStaircaseDefaults(cfg: ContrastConfig): ContrastConfig {
  return { ...DEFAULT_STAIRCASES, ...cfg };
}

export function prLevels(cfg: ContrastConfig): number[] {
  const n = Math.floor(cfg.pr_max_log_cs / cfg.pr_step_log_cs + 1e-9) + 1;
  return Array.from({ length: n }, (_, i) => Math.round(i * cfg.pr_step_log_cs * 1e4) / 1e4);
}

export function baseLetterLogmar(cfg: ContrastConfig, acuityLogmar: number | null): number {
  if (cfg.size_rule === "acuity_relative" && acuityLogmar !== null) {
    return acuityLogmar + cfg.acuity_offset_logmar;
  }
  return letterArcminToLogmar(cfg.pelli_robson_letter_deg * 60);
}
